import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomCard, Label, LegendContainer } from "../../../components/charts/styles";
import Header from "../../../components/charts/components/Header";
import Loader from "../../../components/charts/components/Loader";
import useI18n from "@appsinti/i18n/useI18n";
import COLORS from "../../../../resources/constants/colors";
import IChartData from "../../../../interfaces/IChartData";
import Cash from "../../../../interfaces/Cash";

type MultipleCharts = {
  dataObject: IChartData;
  cash?: Cash | null;
  children?: React.ReactNode;
};

const MultipleCharts: React.FC<MultipleCharts> = ({
  dataObject,
  children,
}) => {
  const { translateObject } = useI18n();
  const firstChildRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('400px');

  const datastacks = useMemo(() => {
    if (!dataObject) return [] as string[];
    const stacks = new Set<string>();
    for (const set of dataObject.labels) {
      if (typeof set === "object" && set !== null && "en" in set && typeof (set as any).en === "string") {
        stacks.add((set as any).en);
      }
    }
    return Array.from(stacks.values());
  }, [dataObject]);

  useEffect(() => {
    if (firstChildRef.current) {
      const height = firstChildRef.current.offsetHeight;
      setMaxHeight(`${height}px`);
    }
  }, [firstChildRef.current]);

  const childrenContainerStyle: React.CSSProperties = {
    maxHeight,
    overflowY: 'auto'
  };

  const [firstChild, ...restChildren] = React.Children.toArray(children);

  return (
    <CustomCard
      background="white"
      header={
        <Header
          title={dataObject?.title ? translateObject(dataObject.title) : ''}
          subtitle={dataObject?.subtitle ? translateObject(dataObject.subtitle) : undefined}
        />
      }
      body={
        datastacks?.length ? (
          <>
            <LegendContainer>
              {datastacks.map((label, i) => (
                <Label
                  key={label}
                  color={COLORS[i]}
                >
                  {label}
                </Label>
              ))}
            </LegendContainer>
            <div style={childrenContainerStyle}>
              <div ref={firstChildRef}>{firstChild}</div>
              {restChildren}
            </div>
          </>
        ) : (
          <Loader />
        )
      }
    />
  );
};

export default MultipleCharts;